/**
 * Event-type enrichment + the design-section matrix — the shared spine every
 * Oxbo-W screen adapts to. Mirrors Aura `tipo_evento_enrichment`
 * (EVENT_TYPE_ENRICHMENT) and Oxbo-Z spec §16b: an event `codigo` resolves to a
 * canonical **event letter** + an **is_conventional** flag, and the letter
 * selects which of the plan design datasets (objetivo / programa / hole_plan /
 * casing / plan_costos / atributos, + Fractura for non-conventional
 * Terminación/Estimulación) apply.
 *
 * This is the single source of truth W0's finder + W1–W6's tabs read, so the
 * app never hard-codes "which tabs" per screen. Same map as the planner seed.
 */

/** Canonical single-letter event class. */
export type EventLetter = "P" | "T" | "L" | "W" | "R" | "A" | "S" | "C" | "V" | "O";

/** A plan design-dataset section key. `fractura` is added at runtime only. */
export type SectionKey =
  | "objetivo"
  | "programa"
  | "hole_plan"
  | "casing"
  | "plan_costos"
  | "atributos"
  | "fractura";

/** codigo → [letter, is_conventional]. Mirrors aura tipo_evento_enrichment. */
const EVENT_ENRICHMENT: Record<string, [EventLetter, boolean]> = {
  PER: ["P", true],
  PERF: ["P", true],
  PNC: ["P", false],
  LPE: ["P", true],
  TERM: ["T", true],
  TER: ["T", true],
  TFR: ["T", false],
  TPO: ["T", false],
  TPR: ["T", false],
  T01: ["T", true],
  PULL: ["L", true],
  PLL: ["L", true],
  WOP: ["L", true],
  WORK: ["W", true],
  WOV: ["W", true],
  RGL: ["R", true],
  ABN: ["A", true],
  SKL: ["S", true],
  WRL: ["S", true],
  CTBG: ["C", true],
  STW: ["V", true],
  OPT: ["O", true],
};

/** Human label per letter (ES — the field vernacular). */
export const EVENT_LETTER_NAME: Record<EventLetter, string> = {
  P: "Perforación",
  T: "Terminación",
  L: "Pulling",
  W: "Workover / Reparación",
  R: "Reglaje",
  A: "Abandono",
  S: "Sidetrack / Reentrada",
  C: "Coiled Tubing",
  V: "Estimulación",
  O: "Optimización",
};

/** The `--op-*` design token that colors each letter's operation. */
export const EVENT_LETTER_OP_VAR: Record<EventLetter, string> = {
  P: "--op-drilling",
  T: "--op-completion",
  L: "--op-pulling",
  W: "--op-workover",
  R: "--op-workover",
  A: "--op-pad",
  S: "--oxbo-m",
  C: "--op-ct",
  V: "--op-unconv",
  O: "--op-pad",
};

/** Which design sections apply to each event letter (before fractura runtime add). */
const SECTION_MATRIX: Record<EventLetter, SectionKey[]> = {
  P: ["objetivo", "programa", "hole_plan", "casing", "plan_costos", "atributos"],
  T: ["objetivo", "programa", "casing", "plan_costos", "atributos"],
  L: ["objetivo", "programa", "plan_costos", "atributos"],
  W: ["objetivo", "programa", "casing", "plan_costos", "atributos"],
  R: ["objetivo", "programa", "plan_costos", "atributos"],
  A: ["objetivo", "programa", "casing", "plan_costos", "atributos"],
  S: ["objetivo", "programa", "hole_plan", "casing", "plan_costos", "atributos"],
  C: ["objetivo", "programa", "plan_costos", "atributos"],
  V: ["objetivo", "programa", "plan_costos", "atributos"],
  O: ["objetivo", "programa", "plan_costos", "atributos"],
};

export interface EventEnrichment {
  letter: EventLetter | null;
  isConventional: boolean | null;
  name: string;
}

/** Resolve an event `codigo` to its letter + conventional flag + human name. */
export function enrichEventCode(code: string | null | undefined): EventEnrichment {
  const key = (code ?? "").toUpperCase().trim();
  const hit = EVENT_ENRICHMENT[key];
  if (!hit) return { letter: null, isConventional: null, name: "—" };
  const [letter, isConventional] = hit;
  return { letter, isConventional, name: EVENT_LETTER_NAME[letter] };
}

/**
 * The ordered design sections that apply to an event, given its letter and
 * conventional flag. Fractura is appended for non-conventional Terminación (T)
 * or Estimulación (V) — the unconventional stages workflow. Unknown letters
 * fall back to the light Optimización set so a screen never renders zero tabs.
 */
export function sectionsFor(
  letter: EventLetter | null,
  isConventional: boolean | null,
): SectionKey[] {
  const base = (letter && SECTION_MATRIX[letter]) || SECTION_MATRIX.O;
  const sections = [...base];
  const isUnconv = isConventional === false;
  if (isUnconv && (letter === "T" || letter === "V")) {
    // Fractura slots right after programa in the authoring flow.
    const at = sections.indexOf("programa");
    sections.splice(at >= 0 ? at + 1 : sections.length, 0, "fractura");
  }
  return sections;
}

/** True if a given section applies to the event. */
export function sectionApplies(
  section: SectionKey,
  letter: EventLetter | null,
  isConventional: boolean | null,
): boolean {
  return sectionsFor(letter, isConventional).includes(section);
}
